/* eslint-disable max-len */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { logOutTry } from '@app/redux/auth/actions';
import { EVENTS, trackEvent } from '@app/segment';
import useUrlPrefix from '@app/hooks/useUrlPrefix';
import { BrowseSwitcherLink, ProductSwitcherLink } from '@app/router';
import useMobileNavOpen from '@app/shell/SiteHeader/shared/hooks/useMobileNavOpen';
import { ChevronRightIcon } from '@stockx/icons';
import {
  SubMenuContainer,
  SubMenuTitle,
  SubMenuLinkContainer,
  SubMenuLink,
  SubMenuFooter,
  SubMenuItem,
  SubMenuItemTitle,
  Chevron,
} from './SubMenuContent.styles';
import AccountMenu from '../../../../menu/AccountMenu';
import AboutMenu from '../../../../menu/AboutMenu';

interface ISubMenuItem {
  urlKey?: string;
  decorate: string;
  title: string;
  isProduct?: boolean;
  submenu?: ISubMenuItem[];
}

export interface SubMenuProps {
  activeSubMenu: {
    title: string;
    link: string;
    submenu: ISubMenuItem[];
  };
  handleSubMenuCloseClick: Function;
  handleItemOnClick: Function;
}

const SubMenuContent: React.FC<SubMenuProps> = ({
  activeSubMenu,
  handleSubMenuCloseClick,
  handleItemOnClick,
}) => {
  const { handleClose, handleToggle } = useMobileNavOpen();

  const { t } = useTranslation();
  const urlPrefix = useUrlPrefix();

  const dispatch = useDispatch();

  const dispatchAuthUniversalLogout = useCallback(
    () => dispatch(logOutTry()),
    [dispatch],
  );

  const handleSubMenuClick = useCallback(
    (urlKey) => {
      if (urlKey === 'signout') {
        dispatchAuthUniversalLogout();
      }

      trackEvent(EVENTS.NAVIGATION_ITEM_CLICKED, {
        label: urlKey,
      });
      handleToggle();
      setTimeout(() => handleSubMenuCloseClick(), 750);
    },
    [handleToggle, handleSubMenuCloseClick, dispatchAuthUniversalLogout],
  );

  if (activeSubMenu?.title === 'My Account') {
    return (
      <AccountMenu
        title={activeSubMenu?.title}
        handleClose={handleClose}
        format={'list'}
      />
    );
  }

  if (activeSubMenu?.title === 'About') {
    return <AboutMenu title={activeSubMenu?.title} />;
  }

  return (
    <>
      <SubMenuContainer>
        <SubMenuTitle>
          {activeSubMenu.link ? (
            <BrowseSwitcherLink
              to={`${urlPrefix}${activeSubMenu.link}`}
              onClick={handleSubMenuClick}
            >
              {t(activeSubMenu.title)}
            </BrowseSwitcherLink>
          ) : (
            t(activeSubMenu.title)
          )}
        </SubMenuTitle>
        <SubMenuLinkContainer>
          {activeSubMenu?.submenu?.map((menuItem, index) =>
            menuItem.submenu ? (
              <SubMenuItem>
                <SubMenuItemTitle onClick={() => handleItemOnClick(menuItem)}>
                  {t(menuItem.title)}
                </SubMenuItemTitle>
                <Chevron>
                  <ChevronRightIcon verticalAlign="-0.125em" />
                </Chevron>
              </SubMenuItem>
            ) : (
              <SubMenuLink key={index}>
                {menuItem.isProduct ? (
                  <ProductSwitcherLink
                    to={
                      menuItem.urlKey !== 'signout'
                        ? `${urlPrefix}${menuItem.urlKey}`
                        : ''
                    }
                    onClick={() => handleSubMenuClick(menuItem.urlKey)}
                    className={menuItem.decorate && 'decorated'}
                  >
                    {t(menuItem.title)}
                  </ProductSwitcherLink>
                ) : (
                  <BrowseSwitcherLink
                    to={
                      menuItem.urlKey !== 'signout'
                        ? `${urlPrefix}${menuItem.urlKey}`
                        : ''
                    }
                    onClick={() => handleSubMenuClick(menuItem.urlKey)}
                    className={menuItem.decorate && 'decorated'}
                  >
                    {t(menuItem.title)}
                  </BrowseSwitcherLink>
                )}
              </SubMenuLink>
            ),
          )}
        </SubMenuLinkContainer>
      </SubMenuContainer>
      <SubMenuFooter />
    </>
  );
};

export default SubMenuContent;
